import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";
import { useDocumentsLists } from "@/hooks/useDocumentsLists";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

interface AssignRequiredDocumentsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  talentId: string;
  currentRequiredDocuments: {
    required_document_types?: number[];
    required_documents_lists?: string[];
  } | null;
  onSuccess: (newRequiredDocuments: any) => void;
}

export function AssignRequiredDocumentsDialog({
  isOpen,
  onOpenChange,
  talentId,
  currentRequiredDocuments,
  onSuccess
}: AssignRequiredDocumentsDialogProps) {
  const { toast } = useToast();
  const { data: documentTypes = [], isLoading: isLoadingTypes } =
    useDocumentTypes();
  const { data: documentsLists = [], isLoading: isLoadingLists } =
    useDocumentsLists();

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedTypes(
        (currentRequiredDocuments?.required_document_types || []).map(String)
      );
      setSelectedLists(
        (currentRequiredDocuments?.required_documents_lists || []).map(String)
      );
    }
  }, [isOpen, currentRequiredDocuments]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // De-duplicate logic:
      // Find all document IDs that are already included in the selected lists
      const idsFromLists = new Set<string>();
      for (const listId of selectedLists) {
        const list = documentsLists.find(
          (l) => String(l.id) === String(listId)
        );
        if (list && list.documents) {
          list.documents.forEach((id) => idsFromLists.add(String(id)));
        }
      }

      // Filter out explicitly selected types that are already provided by lists
      const finalSelectedTypes = selectedTypes
        .map(Number)
        .filter((id) => !idsFromLists.has(String(id)));

      const newRequiredDocuments = {
        required_document_types: finalSelectedTypes,
        required_documents_lists: selectedLists.map(Number) // Assuming the IDs are numbers, we safely parse them to store purely
      };

      const { error } = await supabase
        .from("talent_profiles")
        .update({ required_documents: newRequiredDocuments })
        .eq("id", talentId);

      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "Erforderliche Dokumente zugewiesen."
      });

      onSuccess(newRequiredDocuments);
      onOpenChange(false);
    } catch (error) {
      console.error("Error assigning required documents:", error);
      toast({
        title: "Fehler",
        description: "Zuweisung fehlgeschlagen.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const typesOptions = documentTypes.map((type) => ({
    label: type.name || "Unbenanntes Dokument",
    value: type.id.toString()
  }));

  const listsOptions = documentsLists.map((list) => ({
    label: list.name,
    value: String(list.id)
  }));

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] overflow-visible">
        <DialogHeader>
          <DialogTitle>Erforderliche Dokumente zuweisen</DialogTitle>
          <DialogDescription>
            Weise dem Talent Dokumentenlisten oder einzelne Dokumente zu, die
            eingereicht werden müssen.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label>Dokumentenlisten</Label>
            <p className="text-sm text-gray-500 mb-2">
              Wähle komplette Listen aus (z.B. "Migration nach Bayern").
            </p>
            <MultiSelect
              options={listsOptions}
              selectedValues={selectedLists}
              onValueChange={setSelectedLists}
              placeholder={
                isLoadingLists ? "Lade Listen..." : "Listen auswählen"
              }
              modalPopover={true}
              maxCount={5}
            />
          </div>

          <div className="grid gap-2">
            <Label>Zusätzliche Einzelne Dokumente</Label>
            <p className="text-sm text-gray-500 mb-2">
              Wähle einzelne Dokumente. (Werden ignoriert, falls sie bereits in
              einer gewählten Liste enthalten sind.)
            </p>
            <MultiSelect
              options={typesOptions}
              selectedValues={selectedTypes}
              onValueChange={setSelectedTypes}
              placeholder={
                isLoadingTypes ? "Lade Dokumente..." : "Dokumente auswählen"
              }
              modalPopover={true}
              maxCount={5}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoadingTypes || isLoadingLists}
          >
            {isSaving ? "Speichere..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
